import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, ShoppingCart } from 'lucide-react';
import { Course } from '../../types/course';
import { BASE_URL } from '../../apiConfig';
import { useAuth } from '../../contexts/AuthContext';
import { cardTheme } from '../../styles/cardTheme';
import { getCoursePath } from '../../utils/courseSlug';

interface SuggestedCourseCardProps {
  course: Course;
  variant?: 'default' | 'carousel';
  compact?: boolean;
}

const getCourseImageUrl = (course: Course): string => {
  if (!course.imagePath) return '/default-course.jpg';
  if (course.imagePath.startsWith('http')) return course.imagePath;
  const path = course.imagePath.startsWith('/') ? course.imagePath.slice(1) : course.imagePath;
  return `${BASE_URL}${path}`;
};

/** Carousel card — landscape image with stacked data panel */
const SuggestedCourseCard: React.FC<SuggestedCourseCardProps> = ({
  course,
  variant = 'default',
  compact = false,
}) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const isFree = !(Number(course.price) > 0);
  const formattedPrice = Number(course.price) > 0 ? Number(course.price).toLocaleString('ar-EG') : '0';
  const isCarousel = variant === 'carousel';
  const isCompact = isCarousel && compact;

  const handleSubscribe = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Free courses skip checkout — open the player (or login first)
    if (isFree) {
      if (!isAuthenticated) {
        navigate('/login', {
          state: { redirectTo: `/course-player/${course.id}` },
        });
        return;
      }
      navigate(`/course-player/${course.id}`);
      return;
    }

    if (!isAuthenticated) {
      navigate('/login', {
        state: { redirectTo: '/checkout', course, courseId: course.id },
      });
      return;
    }
    navigate('/checkout', { state: { course, courseId: course.id } });
  };

  if (!isCarousel) {
    return (
      <article className={`group ${cardTheme.courseCard} flex flex-col lg:grid lg:grid-cols-[minmax(220px,34%)_1fr] min-w-0`}>
        <div className="relative aspect-[16/10] lg:aspect-auto lg:min-h-[260px] overflow-hidden bg-green-50 shrink-0">
          <img
            src={getCourseImageUrl(course)}
            alt={`كورس ${course.courseName} - منصة نهج الأزهر - ${isFree ? 'مجاني' : `بسعر ${formattedPrice} جنيه`}`}
            title={course.courseName}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.src = '/default-course.jpg';
            }}
          />
          {isFree && (
            <span className="absolute top-3 right-3 z-10 bg-gold-500 text-green-950 text-xs font-bold px-3 py-1 rounded-full">
              مجاني
            </span>
          )}
        </div>
        <div className="flex flex-col flex-1 p-4 sm:p-5 lg:p-6 border-t-4 lg:border-t-0 lg:border-r-4 border-gold-500">
          <h3 className={`text-base sm:text-lg lg:text-xl font-bold mb-2 line-clamp-2 ${cardTheme.title}`}>
            {course.courseName}
          </h3>
          <p className="text-sm text-gray-600 line-clamp-2 lg:line-clamp-3 mb-4 flex-1">
            {course.description || 'كورس تعليمي مميز لطلاب الثانوية الأزهرية'}
          </p>
          <div className="mt-auto flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-3 border-t border-green-100">
            <div>
              {isFree ? (
                <span className={`text-lg font-bold ${cardTheme.price}`}>مجاناً</span>
              ) : (
                <div className="flex items-baseline gap-1">
                  <span className="text-lg font-bold text-green-800">{formattedPrice}</span>
                  <span className="text-sm font-semibold text-gold-600">جنيه</span>
                </div>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Link
                to={getCoursePath(course)}
                className={`inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold ${cardTheme.btnOutline}`}
              >
                <BookOpen className="w-4 h-4" />
                التفاصيل
              </Link>
              <button
                type="button"
                onClick={handleSubscribe}
                className={`inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold ${cardTheme.btnPrimary}`}
              >
                <ShoppingCart className="w-4 h-4" />
                {isFree ? 'اشترك مجاناً' : 'اشترك الآن'}
              </button>
            </div>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="suggested-course-wireframe coverflow-slide-card group relative h-full w-full">
      <div className="relative flex h-full flex-col overflow-visible rounded-2xl border border-green-100 bg-white shadow-sm transition-all duration-300 group-hover:border-gold-300 group-hover:shadow-lg sm:pb-32">
        <div className="relative aspect-[16/10] w-full shrink-0 overflow-hidden rounded-t-2xl bg-green-50 sm:aspect-[16/9]">
          <img
            src={getCourseImageUrl(course)}
            alt={`كورس ${course.courseName} - منصة نهج الأزهر - ${isFree ? 'مجاني' : `بسعر ${formattedPrice} جنيه`}`}
            title={course.courseName}
            className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.src = '/default-course.jpg';
            }}
          />
          {isFree && (
            <span
              className={`absolute top-2 right-2 z-10 rounded-full bg-gold-500 font-bold text-green-950 ${
                isCompact ? 'px-2 py-0.5 text-[10px]' : 'px-3 py-1 text-xs'
              }`}
            >
              مجاني
            </span>
          )}
        </div>

        {/* Data panel — slight overlap, shifted up & left */}
        <div
          className={`swiper-no-swiping relative z-20 -mt-5 ml-1 mr-6 mb-3 flex flex-1 flex-col rounded-xl border border-green-100 bg-white shadow-md transition-all duration-300 group-hover:border-gold-300 group-hover:shadow-lg sm:absolute sm:bottom-2 sm:left-1 sm:right-6 sm:mx-0 sm:mb-0 sm:-mt-0 ${
            isCompact ? 'p-3.5 sm:p-4' : 'p-4 sm:p-5'
          }`}
        >
          <h3
            className={`mb-1.5 line-clamp-2 font-bold ${cardTheme.title} ${
              isCompact ? 'text-sm sm:text-base' : 'text-base sm:text-lg'
            }`}
          >
            {course.courseName}
          </h3>
          <p
            className={`mb-2 line-clamp-2 leading-relaxed text-gray-600 sm:mb-3 ${
              isCompact ? 'text-xs sm:text-sm' : 'text-sm sm:text-base'
            }`}
          >
            {course.description || 'كورس تعليمي مميز لطلاب الثانوية الأزهرية'}
          </p>
          <div className="mb-2 h-0.5 w-12 bg-gold-400 sm:mb-3" />

          <div className={`mb-2.5 ${isCompact ? '' : 'sm:mb-3'}`}>
            {isFree ? (
              <span className={`font-bold ${cardTheme.price} ${isCompact ? 'text-sm' : 'text-base'}`}>
                مجاناً
              </span>
            ) : (
              <div className="flex items-baseline gap-1">
                <span className={`font-bold text-green-800 ${isCompact ? 'text-sm sm:text-base' : 'text-base'}`}>
                  {formattedPrice}
                </span>
                <span className={`font-semibold text-gold-600 ${isCompact ? 'text-xs sm:text-sm' : 'text-sm'}`}>
                  جنيه
                </span>
              </div>
            )}
          </div>

          <div className={`mt-auto grid grid-cols-2 ${isCompact ? 'gap-2' : 'gap-2.5'}`}>
            <Link
              to={getCoursePath(course)}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              className={`swiper-no-swiping relative z-30 inline-flex min-h-10 items-center justify-center rounded-lg font-semibold ${cardTheme.btnOutline} ${
                isCompact ? 'gap-1 px-2 py-2 text-[11px] sm:text-xs' : 'gap-1.5 px-3 py-2.5 text-xs sm:text-sm'
              }`}
            >
              <BookOpen className={isCompact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
              التفاصيل
            </Link>
            <button
              type="button"
              onClick={handleSubscribe}
              onPointerDown={(e) => e.stopPropagation()}
              className={`swiper-no-swiping relative z-30 inline-flex min-h-10 items-center justify-center rounded-lg font-semibold ${cardTheme.btnPrimary} ${
                isCompact ? 'gap-1 px-2 py-2 text-[11px] sm:text-xs' : 'gap-1.5 px-3 py-2.5 text-xs sm:text-sm'
              }`}
            >
              <ShoppingCart className={isCompact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
              {isFree ? 'مجاناً' : 'اشترك'}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
};

export default SuggestedCourseCard;
